import * as TabsPrimitive from "@radix-ui/react-tabs";
import {
    ComponentPropsWithoutRef,
    CSSProperties,
    ReactNode,
    Ref,
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { cx } from "../../utils/cx";
import styles from "./Tabs.module.scss";

export type TabsOrientation = "horizontal" | "vertical";

type TabsContextValue = {
    orientation: TabsOrientation;
};

const TabsContext = createContext<TabsContextValue>({
    orientation: "horizontal",
});

const useTabsContext = () => useContext(TabsContext);

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
    if (!ref) return;
    if (typeof ref === "function") {
        ref(value);
    } else {
        (ref as { current: T | null }).current = value;
    }
}

const ChevronPrev = () => (
    <svg aria-hidden fill="none" height="16" viewBox="0 0 16 16" width="16">
        <path
            d="M10 3.5 5.5 8 10 12.5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6"
        />
    </svg>
);

const ChevronNext = () => (
    <svg aria-hidden fill="none" height="16" viewBox="0 0 16 16" width="16">
        <path
            d="M6 3.5 10.5 8 6 12.5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.6"
        />
    </svg>
);

export type TabsProps = Omit<
    ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
    "orientation"
> & {
    /** Tab list layout. Default `horizontal`. */
    orientation?: TabsOrientation;
    children: ReactNode;
};

/**
 * Treez DS — Tabs (Wave 2).
 *
 * Built on `@radix-ui/react-tabs`. Compound API:
 * `Tabs` / `Tabs.List` / `Tabs.Trigger` / `Tabs.Content`.
 *
 * Horizontal lists scroll (no wrap) with edge chevrons when content overflows.
 * Prefer `SegmentControl` for 2–5 options without panels, and
 * `Navigation` for settings side rails.
 */
function TabsRoot({
    className,
    orientation = "horizontal",
    children,
    ...rest
}: TabsProps) {
    return (
        <TabsContext.Provider value={{ orientation }}>
            <TabsPrimitive.Root
                className={cx(styles.root, className)}
                data-orientation={orientation}
                data-ui="tabs"
                orientation={orientation}
                {...rest}
            >
                {children}
            </TabsPrimitive.Root>
        </TabsContext.Provider>
    );
}
TabsRoot.displayName = "Tabs";

export type TabsListProps = ComponentPropsWithoutRef<
    typeof TabsPrimitive.List
> & {
    children: ReactNode;
    /**
     * Stick the tab list to the top of the nearest scrollport while content scrolls.
     * Default `top` cancels Layout `.routesColumn` padding (`space-md`).
     */
    sticky?: boolean;
    /**
     * CSS `top` when `sticky` is set.
     * @default "calc(-1 * var(--ds-space-md))"
     */
    stickyTop?: string;
};

const List = forwardRef<HTMLDivElement, TabsListProps>(function TabsList(
    { className, children, sticky = false, stickyTop, ...rest },
    forwardedRef,
) {
    const { orientation } = useTabsContext();
    const listRef = useRef<HTMLDivElement | null>(null);
    const [canScrollStart, setCanScrollStart] = useState(false);
    const [canScrollEnd, setCanScrollEnd] = useState(false);

    const updateOverflow = useCallback(() => {
        const el = listRef.current;
        if (!el) return;

        const isVertical = orientation === "vertical";
        const max = isVertical
            ? el.scrollHeight - el.clientHeight
            : el.scrollWidth - el.clientWidth;
        const pos = isVertical ? el.scrollTop : el.scrollLeft;
        const epsilon = 1;

        setCanScrollStart(pos > epsilon);
        setCanScrollEnd(max > epsilon && pos < max - epsilon);
    }, [orientation]);

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;

        updateOverflow();

        const onScroll = () => updateOverflow();
        el.addEventListener("scroll", onScroll, { passive: true });

        const resizeObserver = new ResizeObserver(() => updateOverflow());
        resizeObserver.observe(el);
        Array.from(el.children).forEach(child => {
            resizeObserver.observe(child);
        });

        const mutationObserver = new MutationObserver(records => {
            const stateChanged = records.some(
                r =>
                    r.type === "attributes" && r.attributeName === "data-state",
            );
            if (stateChanged) {
                const active = el.querySelector(
                    '[data-state="active"]',
                ) as HTMLElement | null;
                active?.scrollIntoView({
                    block: "nearest",
                    inline: "nearest",
                    behavior: "smooth",
                });
            }
            updateOverflow();
        });
        mutationObserver.observe(el, {
            attributes: true,
            subtree: true,
            attributeFilter: ["data-state"],
            childList: true,
        });

        return () => {
            el.removeEventListener("scroll", onScroll);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [updateOverflow, children]);

    const scrollByPage = (direction: -1 | 1) => {
        const el = listRef.current;
        if (!el) return;
        const isVertical = orientation === "vertical";
        const amount =
            (isVertical ? el.clientHeight : el.clientWidth) * 0.75 * direction;
        el.scrollBy({
            left: isVertical ? 0 : amount,
            top: isVertical ? amount : 0,
            behavior: "smooth",
        });
    };

    const setListRef = (node: HTMLDivElement | null) => {
        listRef.current = node;
        assignRef(forwardedRef, node);
    };

    const showControls = canScrollStart || canScrollEnd;

    return (
        <div
            className={styles.listShell}
            data-can-scroll-end={canScrollEnd || undefined}
            data-can-scroll-start={canScrollStart || undefined}
            data-orientation={orientation}
            data-overflow={showControls || undefined}
            data-sticky={sticky || undefined}
            style={
                sticky && stickyTop
                    ? ({
                          ["--tabs-list-sticky-top" as string]: stickyTop,
                      } as CSSProperties)
                    : undefined
            }
        >
            {showControls ? (
                <button
                    aria-hidden={!canScrollStart}
                    aria-label={
                        orientation === "vertical"
                            ? "Scroll tabs up"
                            : "Scroll tabs left"
                    }
                    className={cx(styles.scrollBtn, styles.scrollStart)}
                    disabled={!canScrollStart}
                    onClick={() => scrollByPage(-1)}
                    tabIndex={canScrollStart ? 0 : -1}
                    type="button"
                >
                    <ChevronPrev />
                </button>
            ) : null}

            <TabsPrimitive.List
                className={cx(styles.list, className)}
                data-orientation={orientation}
                ref={setListRef}
                {...rest}
            >
                {children}
            </TabsPrimitive.List>

            {showControls ? (
                <button
                    aria-hidden={!canScrollEnd}
                    aria-label={
                        orientation === "vertical"
                            ? "Scroll tabs down"
                            : "Scroll tabs right"
                    }
                    className={cx(styles.scrollBtn, styles.scrollEnd)}
                    disabled={!canScrollEnd}
                    onClick={() => scrollByPage(1)}
                    tabIndex={canScrollEnd ? 0 : -1}
                    type="button"
                >
                    <ChevronNext />
                </button>
            ) : null}
        </div>
    );
});
List.displayName = "Tabs.List";

export type TabsTriggerProps = ComponentPropsWithoutRef<
    typeof TabsPrimitive.Trigger
> & {
    /** Optional secondary line under the label (e.g. schedule date). */
    description?: ReactNode;
    children: ReactNode;
};

const Trigger = forwardRef<HTMLButtonElement, TabsTriggerProps>(
    function TabsTrigger({ className, description, children, ...rest }, ref) {
        return (
            <TabsPrimitive.Trigger
                className={cx(styles.trigger, className)}
                ref={ref}
                {...rest}
            >
                <span className={styles.label}>{children}</span>
                {description != null ? (
                    <span className={styles.description}>{description}</span>
                ) : null}
            </TabsPrimitive.Trigger>
        );
    },
);
Trigger.displayName = "Tabs.Trigger";

export type TabsContentProps = ComponentPropsWithoutRef<
    typeof TabsPrimitive.Content
> & {
    children: ReactNode;
};

const Content = forwardRef<HTMLDivElement, TabsContentProps>(
    function TabsContent({ className, children, ...rest }, ref) {
        return (
            <TabsPrimitive.Content
                className={cx(styles.content, className)}
                ref={ref}
                {...rest}
            >
                {children}
            </TabsPrimitive.Content>
        );
    },
);
Content.displayName = "Tabs.Content";

export const Tabs = Object.assign(TabsRoot, {
    List,
    Trigger,
    Content,
});
